/**
 * DefaultPickupLocationPrompt
 * Visas efter inloggning om användaren saknar en förvald utlämningsplats men
 * har tillgång till en eller flera utlämningsplatser i sin organisation.
 * Låter användaren välja sin förvalda (default) utlämningsplats.
 * Visas inte när en global admin har växlat till användaren. Ctrl+Shift+M
 * stänger rutan utan att spara i det läget.
 *
 * Fler profilinställningar kan komplettera denna ruta framöver.
 */

import React, { useEffect, useRef, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { BaseModal } from '../common/BaseModal';
import { useTranslation } from '@/store/languageStore';
import { useAuthStore } from '@/store/authStore';
import { userService } from '@/services/user/userService';
import { bookingApi, formatPickupLocationOption, PickupLocation } from '@/services/booking/bookingApi';
import {
  isPickupPromptDismissShortcut,
  shouldPromptForDefaultPickup,
} from './defaultPickupPromptPolicy';

// In-memory-flagga (per sidladdning) som hindrar dubbel-kontroll vid remount/
// navigering i SPA:n. Återställs vid full omladdning så att den obligatoriska
// rutan visas igen tills ett förval faktiskt sparats i databasen.
let checkedThisLoad = false;

const DefaultPickupLocationPrompt: React.FC = () => {
  const { t } = useTranslation();
  const isImpersonating = useAuthStore((s) => s.impersonation?.isImpersonating === true);

  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(false);
  isOpenRef.current = isOpen;
  const [pickupLocations, setPickupLocations] = useState<PickupLocation[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isImpersonating) {
      setIsOpen(false);
      return;
    }

    let cancelled = false;

    const check = async () => {
      if (checkedThisLoad) return;
      checkedThisLoad = true;

      try {
        const prefs = await userService.getPreferences();
        if (cancelled) return;

        const locations = prefs?.defaultPickupLocationId
          ? []
          : await bookingApi
              .getPickupLocations(false, true)
              .catch(() => [] as PickupLocation[]);
        if (cancelled) return;

        if (
          !shouldPromptForDefaultPickup({
            isImpersonating: false,
            hasDefaultPickup: Boolean(prefs?.defaultPickupLocationId),
            pickupLocationCount: locations.length,
          })
        ) {
          return;
        }

        setPickupLocations(locations);
        setIsOpen(true);
      } catch {
        // Tyst – prompten är icke-kritisk och får aldrig blockera appen.
        checkedThisLoad = false;
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [isImpersonating]);

  useEffect(() => {
    if (!isImpersonating) return;

    const onKey = (event: KeyboardEvent) => {
      if (!isOpenRef.current || !isPickupPromptDismissShortcut(event)) return;
      event.preventDefault();
      event.stopPropagation();
      setIsOpen(false);
    };

    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [isImpersonating]);

  const handleSave = async () => {
    if (!selectedId) {
      setError(t('profile.selectPickupLocationRequired'));
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await userService.updatePreferences({ defaultPickupLocationId: selectedId });
      setIsOpen(false);
    } catch (err: any) {
      setError(err?.response?.data?.error || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <BaseModal
      isOpen={isOpen}
      // Obligatorisk ruta: användaren måste välja en utlämningsplats. Ingen
      // stängning via X, backdrop eller ESC – endast Spara stänger rutan.
      onClose={() => { /* mandatory – cannot dismiss without saving */ }}
      title={t('profile.pickupPromptTitle')}
      size="md"
      closeOnBackdrop={false}
      showCloseButton={false}
      footer={
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !selectedId}
          className="flex items-center gap-2 px-6 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-primary hover:bg-primary/90 text-white"
        >
          {saving && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
          {saving ? t('common.saving') : t('common.save')}
        </button>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-textSecondary">{t('profile.pickupPromptDescription')}</p>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-textSecondary mb-1">
            {t('profile.defaultPickupLocation')}
          </label>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgDefault text-textDefault focus:ring-2 focus:ring-primary focus:border-primary"
          >
            <option value="">{t('profile.selectPickupLocation')}</option>
            {pickupLocations.map((loc) => (
              <option key={String(loc.id)} value={String(loc.id)}>
                {formatPickupLocationOption(loc, {
                  vehiclePoolSuffix: t('booking.bookingTypePool'),
                  opens: (date) => t('booking.pickupLocationOpens', { date }),
                })}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-textTertiary">{t('profile.defaultPickupLocationHint')}</p>
        </div>
      </div>
    </BaseModal>
  );
};

export default DefaultPickupLocationPrompt;
